import React, {useRef} from 'react'

import {NextSeo} from 'next-seo'
import {useRouter} from 'next/router'
import styled from 'styled-components'
import {useHits} from 'react-instantsearch'
import classNames from 'classnames'
import {palette} from 'styled-tools'
import {media} from 'styled-bootstrap-grid'
import {useUpdateEffect} from 'react-use'
import IndeterminateCircleLineIcon from 'remixicon-react/IndeterminateCircleLineIcon'

import {VariantList} from '@festi/common/api/rest'
import {EmptyResults} from '@festi/common/components/common'
import {getSearchResultsUrl} from '@festi/common/utils/search'
import {useUserPrices} from '@festi/common/utils/rest'
import {onDutyFree} from '@festi/common/constants/channels'
import settings from '@festi/common/constants/settings'
import {useOnScreen} from '@festi/common/utils/useOnScreen'

import CustomRefinement from './CustomRefinement'
import NewProductCard, {
  GridType,
} from 'src/components/cards/redesign/NewProductCard'
import useHeaderHeight from 'src/components/layout/redesign/header/utilites/useHeaderHeight'
import ProductCardLoader from 'src/components/cards/redesign/utilities/ProductCardLoader'
import {EventIndexQueryID} from '../utilites/algoliaEvents'

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  grid-gap: 16px;
  padding-top: 24px;

  &.small {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  }

  &.list {
    display: flex;
    flex-direction: column;
  }
`

const FilterRoot = styled.div`
  min-height: 72px;
`

interface PanelProps {
  height: number
}

const FilterPanel = styled.div<PanelProps>`
  display: flex;
  gap: 16px;
  padding-block: 12px;

  ${media.md`
    padding-block: 16px;
  `}

  &.stickyPanel {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    margin-top: ${(p) => p.height}px;
    padding: 10px 20px;
    background-color: ${palette('backgroundGrey')};
    transition: margin-top 150ms ease-in;
  }
`

const FilterContainer = styled.div`
  width: 100%;
  max-width: 1584px;
  margin: auto;
`

interface HitsProps {
  title: string
  query: string
  gridType?: GridType
  description?: string
  attrNameMap?: Record<string, string>
  isCategoryPage?: boolean
  setNumberOfHits?: (hits: number) => void
}

export default function CustomHits({
  title,
  gridType,
  description,
  attrNameMap,
  isCategoryPage,
  setNumberOfHits,
}: HitsProps) {
  const {query, pathname} = useRouter()
  const hits = useHits<VariantList>()
  const {headerHeight} = useHeaderHeight()

  const ref = useRef(null)
  const offset = pathname === '/leit' ? headerHeight + 50 : headerHeight + 150
  const isInView = useOnScreen(ref, {topOffset: `-${offset}px`})

  const variants = hits?.items
  const page = hits?.results?.page || 1

  const {data: userPrices} = useUserPrices(variants?.map((v) => v.sku))

  const baseUrl = onDutyFree ? settings.dutyfreeUrl : settings.baseUrl

  const facetsOrder =
    hits?.results?.renderingContent?.facetOrdering?.facets?.order

  useUpdateEffect(() => {
    setNumberOfHits?.(hits?.results?.nbHits || 0)
  }, [hits?.results?.nbHits])

  return (
    <>
      <FilterRoot ref={ref}>
        <FilterPanel
          height={headerHeight}
          className={classNames({stickyPanel: !isInView})}
        >
          <FilterContainer>
            <CustomRefinement facets={facetsOrder} attrNameMap={attrNameMap} />
          </FilterContainer>
        </FilterPanel>
      </FilterRoot>

      {!!variants?.length ? (
        <ProductGrid className={gridType}>
          {variants.map((variant) => (
            <NewProductCard
              key={variant.id}
              origin={variant.__queryID}
              variant={variant}
              position={variant.__position}
              gridType={gridType}
              image={{
                url: variant.firstImage.image.webpS || '',
                jpg: variant.firstImage.image.jpgS || '',
                alt: variant.firstImage.alt || variant.name || '',
              }}
              userPrice={userPrices?.[variant.sku]}
            />
          ))}
        </ProductGrid>
      ) : !hits?.results?.queryID ? (
        <ProductGrid className={gridType}>
          {[...Array(20)].map((_, i) => (
            <ProductCardLoader key={i} horizontal={gridType === 'list'} />
          ))}
        </ProductGrid>
      ) : (
        <EmptyResults icon={IndeterminateCircleLineIcon}>
          Engar vörur fundust
        </EmptyResults>
      )}

      <EventIndexQueryID
        index={hits?.results?.index}
        queryID={hits?.results?.queryID}
      />

      <NextSeo
        title={
          hits.results.query
            ? `„${query.q}“ - Leitarniðurstöður síða ${page}`
            : title
        }
        description={description}
        canonical={getSearchResultsUrl({
          origin: baseUrl,
          query,
          isCategoryPage,
        })}
        openGraph={{
          title: query.q
            ? `„${query.q}“ - Leitarniðurstöður síða ${page}“`
            : title,
          description: description,
        }}
        additionalLinkTags={[
          hits?.results.nbPages !== page && {
            rel: 'next',
            href: getSearchResultsUrl({
              origin: baseUrl,
              query,
              isCategoryPage,
              page: page + 1,
            }),
          },
          page !== 1 && {
            rel: 'prev',
            href: getSearchResultsUrl({
              origin: baseUrl,
              query,
              isCategoryPage,
              page: page - 1,
            }),
          },
        ]}
      />
    </>
  )
}
